<template>
  <div id="app">
    <div class="container">
      <!-- Lista de odontogramas do paciente -->
      <b-card v-if="!showOdontogramaForm && !loading">
        <b-card-body>
          <div v-if="odontograms.length === 0" class="text-center py-4">
            <feather-icon
              icon="FileTextIcon"
              size="48"
              class="text-muted mb-2"
            />
            <p class="text-muted">
              Nenhum odontograma encontrado para este paciente.
            </p>
            <b-button variant="primary" @click="createOdontogram">
              Criar Primeiro Odontograma
            </b-button>
          </div>

          <b-list-group v-else>
            <b-list-group-item
              v-for="odontogram in odontograms"
              :key="odontogram.id"
              class="d-flex justify-content-between align-items-center"
            >
              <div>
                <h6 class="mb-1">Odontograma #{{ odontogram.id }}</h6>
                <small> Criado em: {{ formatDate(odontogram.data) }} </small>
              </div>
              <div>
                <b-button
                  variant="outline-primary"
                  size="sm"
                  class="mr-1"
                  title="Editar Odontograma"
                  @click="editOdontogram(odontogram)"
                >
                  <feather-icon icon="EditIcon" />
                </b-button>
                <b-button
                  variant="outline-success"
                  size="sm"
                  title="Visualizar Odontograma"
                  @click="viewOdontogram(odontogram)"
                >
                  <feather-icon icon="EyeIcon" />
                </b-button>
              </div>
            </b-list-group-item>
          </b-list-group>
        </b-card-body>
      </b-card>

      <!-- Loading spinner -->
      <div v-if="loading" class="text-center py-4">
        <b-spinner variant="primary" label="Carregando..." />
        <p class="mt-2 text-muted">Carregando odontogramas...</p>
      </div>

      <!-- Formulário de odontograma (será mostrado quando showOdontogramaForm = true) -->
      <div v-if="showOdontogramaForm">
        <b-button
          class="mb-3"
          variant="outline-secondary"
          @click="showOdontogramaForm = false"
        >
          <feather-icon icon="ArrowLeftIcon" />
          Voltar para Lista
        </b-button>
        <DigitalOdontogram :teeth="teeth" @tooth-updated="updateTooth" />
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import {
  BCard,
  BCardHeader,
  BCardBody,
  BListGroup,
  BListGroupItem,
  BButton,
  BSpinner,
  BBadge,
} from "bootstrap-vue";
import DigitalOdontogram from "./components/DigitalOdontogram.vue";
import odontogramaServices from "@/api/odontograma/odontogramaServices";
export default {
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BListGroup,
    BListGroupItem,
    BButton,
    BSpinner,
    BBadge,
    DigitalOdontogram,
  },
  computed: {
    ...mapState("atendimentoModule", {
      atendimento: (state) => state.atendimento,
    }),
  },

  data() {
    return {
      showOdontogramaForm: false,
      loading: false,
      odontograms: [],
      patientId: null,
      currentDate: this.formatDateToDDMMYYYY(new Date()),
      teeth: [],
    };
  },

  mounted() {
    // Obter o ID do paciente da rota ou de outra fonte
    this.patientId = this.atendimento.registro.id;
    if (this.patientId) {
      this.loadPatientOdontograms();
    }
  },

  methods: {
    /**
     * Formata a data para o formato dd-mm-yyyy
     */
    formatDateToDDMMYYYY(date) {
      if (!date) return "";
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    },

    /**
     * Carrega os odontogramas do paciente usando o serviço getPatientById
     */
    async loadPatientOdontograms() {
      if (!this.patientId) {
        console.error("ID do paciente não encontrado");
        return;
      }

      this.loading = true;
      try {
        // Usar o serviço diretamente ou através do Vuex
        const response = await odontogramaServices.getPatientById(
          this.patientId
        );
        this.odontograms = response.data.content || response.data || [];
        console.log("Odontogramas carregados:", this.odontograms);
      } catch (error) {
        console.error("Erro ao carregar odontogramas do paciente:", error);
        this.$bvToast.toast("Erro ao carregar odontogramas do paciente", {
          title: "Erro",
          variant: "danger",
          solid: true,
        });
      } finally {
        this.loading = false;
      }
    },
    /**
     * Formata a data para exibição
     */
    formatDate(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      return date.toLocaleDateString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    },

    /**
     * Edita um odontograma existente
     */
    editOdontogram(odontogram) {
      this.$store
        .dispatch("odontogramaModule/getById", odontogram.id)
        .then((response) => {
          this.teeth = response.dentes;
          this.showOdontogramaForm = true;
        })
        .catch((error) => {
          console.error("Erro ao carregar odontograma:", error);
        });
    },

    /**
     * Visualiza um odontograma
     */
    viewOdontogram(odontogram) {
      console.log("Visualizando odontograma:", odontogram);
      // Aqui você pode implementar a lógica para visualizar o odontograma
      // Por exemplo, abrir um modal ou navegar para uma página de detalhes
    },

    /**
     * Método legado mantido para compatibilidade
     */
    loadOdontograma() {
      this.showOdontogramaForm = true;
    },
    createOdontogram() {
      this.$store
        .dispatch("odontogramaModule/generate", {
          paciente: this.atendimento.registro.id,
          profissional: this.atendimento.profissional.id,
          data: this.currentDate,
          ativo: true,
        })
        .then((response) => {
          console.log("Odontograma criado:", response);
          this.showOdontogramaForm = true;
        });
    },

    /**
     * Atualiza um dente quando o componente DigitalOdontogram emite o evento
     */
    updateTooth(updatedTooth) {
      const index = this.teeth.findIndex(
        (tooth) => tooth.id === updatedTooth.id
      );
      if (index !== -1) {
        this.teeth.splice(index, 1, updatedTooth);
        console.log("Dente atualizado:", updatedTooth);
      }
    },
  },
};
</script>
